import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { verifyOTP, clearError } from '../../store/slices/authSlice';
import { getPostLoginPath, getRoleHomePath } from '../../utils/postLoginRedirect';
import AuthLayout from './AuthLayout';

const OTPStep = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isLoading, error, isAuthenticated, user } = useSelector((state) => state.auth);

  const [otp, setOtp] = useState('');
  const [timeLeft, setTimeLeft] = useState(300);

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(getRoleHomePath(user?.role_id, user?.practice_id), { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!otp || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    try {
      const result = await dispatch(verifyOTP({ otp }));

      if (verifyOTP.fulfilled.match(result)) {
        navigate(getPostLoginPath(result.payload.user, result.payload.redirectUrl), { replace: true });
      } else if (verifyOTP.rejected.match(result)) {
        toast.error(result.payload || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      console.error('OTP verification error:', err);
      toast.error('An unexpected error occurred. Please try again.');
    }
  };

  const handleResendOTP = () => {
    toast.info('OTP resent to your email');
    setTimeLeft(300);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <AuthLayout
      title="Two-Factor Verification"
      subtitle="Enter the 6-digit verification code sent to your email."
    >
      <form onSubmit={handleSubmit} className="lp-auth-form" noValidate>
        <div className="lp-form-group">
          <label htmlFor="otp" className="lp-label">
            Enter OTP <span className="lp-required">*</span>
          </label>
          <div className="lp-input-container">
            <input
              id="otp"
              type="text"
              value={otp}
              onChange={handleOtpChange}
              placeholder="000000"
              className="lp-input lp-otp-input"
              required
              maxLength={6}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
            />
          </div>
        </div>

        <div className="lp-otp-meta">
          <span className="lp-otp-timer">
            Expires in: <strong>{formatTime(timeLeft)}</strong>
          </span>
          <button
            type="button"
            className="lp-forgot-pass-link"
            onClick={handleResendOTP}
            disabled={timeLeft > 0}
          >
            Resend OTP
          </button>
        </div>

        {error && <p className="lp-form-error">{error}</p>}

        <button
          type="submit"
          className="lp-btn-signin"
          disabled={isLoading || timeLeft === 0 || otp.length !== 6}
        >
          {isLoading && <span className="lp-btn-spinner" />}
          <span>{isLoading ? 'Verifying...' : 'Verify OTP'}</span>
        </button>

        <div className="lp-back-wrap">
          <Link to="/login" className="lp-forgot-pass-link">
            ← Back to Sign In
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
};

export default OTPStep;
